import { useEffect, useRef } from "react";
import { useIsNewNativeApp } from "hooks/useIsApp";

/**
 * Wakes the WKWebView compositor after a property → property SPA transition.
 *
 * Going from PDP B back to PDP A inside the same WebView leaves the screen blank
 * white: the DOM is correct (taps land on the right controls) but the tile
 * compositor never repaints until the user touches the screen. A 1px scroll and
 * back forces the paint. Full diagnosis in the amber-ios-app spec
 * `.kiro/specs/webview-blank-page-on-back/bugfix.md`.
 *
 * This used to live in the native shell, which ran the same JS via
 * `evaluateJavaScript` on every WKWebView `.url` KVO hit — an IPC round trip on
 * every navigation app-wide, including query-param changes on search and filter
 * pages that never had the bug. Keying it off the canonical name instead means it
 * fires only on the transition that actually freezes.
 *
 * Gated to the new native shells (ios_native / android_native). Web, RN and the
 * mini program return on the first line and are unaffected.
 */
const REPAINT_DELAY_MS = 300;

export const usePropertyRepaintNudge = (canonicalName?: string): void => {
  const isNewNativeApp = useIsNewNativeApp();
  const lastCanonical = useRef<string | null>(null);

  useEffect(() => {
    if (!isNewNativeApp || !canonicalName || typeof window === "undefined") return undefined;

    const previous = lastCanonical.current;
    lastCanonical.current = canonicalName;

    // First property in this WebView is a fresh paint — only a PDP → PDP swap freezes.
    if (!previous || previous === canonicalName) return undefined;

    // 300ms lets the new property's DOM commit before we nudge. A further transition
    // inside that window cancels this one rather than queueing a second scroll.
    const timer = window.setTimeout(() => {
      // An offscreen/backgrounded WebView repaints on its own when shown again.
      if (document.hidden) return;

      const y = window.scrollY;
      // At the very top the only direction available is down; anywhere else go up, so
      // the nudge is never a no-op at the bottom of the page. Restore the exact offset
      // rather than scrolling back by the same delta, which drifts at either end.
      window.scrollBy(0, y > 0 ? -1 : 1);
      window.requestAnimationFrame(() => window.scrollTo(0, y));
    }, REPAINT_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [isNewNativeApp, canonicalName]);
};

export default usePropertyRepaintNudge;
